import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { imageTypeOf, type EntryImageView } from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { ImageFileError, prepareImage } from '../editor/imageFiles.ts';

/**
 * An entry's pictures (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.2): thumbnails, a
 * full-size view, and in the editor adding (a file picker or dropped files) and removing
 * with Undo. Pictures are shown only as `blob:` URLs of the bytes, with the type their
 * bytes show (realm-core's getEntry).
 */

/** Over this many bytes of pictures in a realm, a note says saves slow down (every save seals the whole realm). */
export const LARGE_REALM_IMAGE_BYTES = 200 * 1024 * 1024;
const UNDO_MS = 8000;

/** One `blob:` URL per picture, made once and freed when the picture goes or the page does. */
function useImageUrls(images: readonly EntryImageView[]): ReadonlyMap<string, string> {
  const cache = useRef(new Map<string, string>());
  const [, redraw] = useState(0);
  useEffect(() => {
    const urls = cache.current;
    const ids = new Set(images.map((image) => image.id));
    for (const [id, url] of urls) {
      if (!ids.has(id)) {
        URL.revokeObjectURL(url);
        urls.delete(id);
      }
    }
    for (const image of images) {
      if (!urls.has(image.id)) urls.set(image.id, URL.createObjectURL(new Blob([image.data as Uint8Array<ArrayBuffer>], { type: image.mimeType })));
    }
    redraw((n) => n + 1);
  }, [images]);
  useEffect(
    () => () => {
      for (const url of cache.current.values()) URL.revokeObjectURL(url);
      cache.current.clear();
    },
    [],
  );
  return cache.current;
}

export function ImageGallery({ images, onRemove }: { images: readonly EntryImageView[]; onRemove?: (image: EntryImageView) => void }) {
  const urls = useImageUrls(images);
  const [open, setOpen] = useState<EntryImageView | null>(null);
  const openUrl = open ? urls.get(open.id) : undefined;
  return (
    <>
      <ul className="images">
        {images.map((image) => {
          const url = urls.get(image.id);
          return url ? (
            <li key={image.id}>
              <figure>
                <button type="button" className="image-open" onClick={() => setOpen(image)} aria-label={`View ${image.fileName} full size`}>
                  <img src={url} alt={image.fileName} loading="lazy" />
                </button>
                <figcaption>{image.fileName}</figcaption>
              </figure>
              {onRemove && (
                <button type="button" className="quiet" onClick={() => onRemove(image)}>
                  Remove<span className="visually-hidden"> {image.fileName}</span>
                </button>
              )}
            </li>
          ) : null;
        })}
      </ul>
      {open && openUrl && <ImageViewer image={open} url={openUrl} onClose={() => setOpen(null)} />}
    </>
  );
}

/** The full-size picture, in a modal dialog: Escape, the Close button or a click outside closes it. */
function ImageViewer({ image, url, onClose }: { image: EntryImageView; url: string; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    return () => element.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="image-viewer"
      aria-label={image.fileName}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <img src={url} alt={image.fileName} />
      <div className="image-viewer-bar">
        <span>{image.fileName}</span>
        <button type="button" onClick={onClose} autoFocus>
          Close
        </button>
      </div>
    </dialog>
  );
}

/** The editor's Images section. Each picture is written as soon as it's added or removed. */
export function EditImages({ file, entryId, initial, realmImageBytes }: {
  file: string;
  entryId: string;
  initial: readonly EntryImageView[];
  /** Bytes of pictures in the whole realm when the editor opened. */
  realmImageBytes: number;
}) {
  const router = useRouter();
  const [images, setImages] = useState<EntryImageView[]>(() => [...initial]);
  const [busy, setBusy] = useState(0);
  const [notes, setNotes] = useState<{ text: string; error: boolean }[]>([]);
  const [removed, setRemoved] = useState<{ image: EntryImageView; at: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const undoTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const changed = useRef(false);
  const added = useRef(0);
  const input = useRef<HTMLInputElement>(null);

  // The entry page and lists read pictures through the router: reload them once this editor closes.
  useEffect(
    () => () => {
      clearTimeout(undoTimer.current);
      if (changed.current) void router.invalidate();
    },
    [router],
  );
  // Closing the page while a picture is still being written asks first.
  useEffect(() => {
    if (busy === 0) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const message = (e: unknown, fallback: string) => (e instanceof ImageFileError || e instanceof RealmClientError ? e.message : fallback);

  async function add(files: readonly File[]) {
    if (files.length === 0) return;
    setNotes([]);
    setBusy((n) => n + files.length);
    for (const picked of files) {
      try {
        const prepared = await prepareImage(picked);
        const { id, created } = await realms.addImage(file, entryId, { fileName: prepared.fileName, data: prepared.data });
        changed.current = true;
        added.current += prepared.data.byteLength;
        const mimeType = imageTypeOf(prepared.data)!;
        setImages((list) => [...list, { id, fileName: prepared.fileName, mimeType, data: prepared.data, created }]);
        if (prepared.convertedFrom) {
          const from = prepared.convertedFrom === 'image/webp' ? 'WebP' : 'BMP';
          setNotes((list) => [...list, { text: `${picked.name} was turned from ${from} into PNG, so Realmery on the desktop can show it.`, error: false }]);
        }
      } catch (e) {
        setNotes((list) => [...list, { text: `${picked.name}: ${message(e, 'The picture could not be added.')}`, error: true }]);
      } finally {
        setBusy((n) => n - 1);
      }
    }
  }

  async function remove(image: EntryImageView) {
    setNotes([]);
    try {
      await realms.removeImage(file, image.id);
    } catch (e) {
      setNotes([{ text: message(e, 'The picture could not be removed.'), error: true }]);
      return;
    }
    changed.current = true;
    added.current -= image.data.byteLength;
    setImages((list) => list.filter((other) => other.id !== image.id));
    setRemoved({ image, at: images.findIndex((other) => other.id === image.id) });
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setRemoved(null), UNDO_MS);
  }

  async function undo() {
    if (!removed) return;
    clearTimeout(undoTimer.current);
    const { image, at } = removed;
    setRemoved(null);
    try {
      await realms.addImage(file, entryId, { fileName: image.fileName, data: image.data }, image.id, image.created);
    } catch (e) {
      setNotes([{ text: message(e, 'The picture could not be put back.'), error: true }]);
      return;
    }
    added.current += image.data.byteLength;
    setImages((list) => [...list.slice(0, at), image, ...list.slice(at)]);
  }

  const hasFiles = (event: DragEvent) => event.dataTransfer.types.includes('Files');

  return (
    <section
      className={dragging ? 'editor-section image-drop dragging' : 'editor-section image-drop'}
      aria-labelledby="images-heading"
      onDragOver={(event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDragging(false);
        void add([...event.dataTransfer.files]);
      }}
    >
      <h2 id="images-heading">Images</h2>
      {images.length === 0 && <p className="meta">No pictures yet. Add them here, or drop picture files on this section.</p>}
      <ImageGallery images={images} onRemove={(image) => void remove(image)} />
      {removed && (
        <p className="meta" role="status">
          Removed {removed.image.fileName}.{' '}
          <button type="button" className="link-button" onClick={() => void undo()}>
            Undo
          </button>
        </p>
      )}
      {busy > 0 && (
        <p className="meta" role="status">
          Adding {busy === 1 ? 'a picture' : `${busy} pictures`}…
        </p>
      )}
      {notes.map((note, i) => (
        <p key={i} className={note.error ? 'notice-error' : 'meta'} role={note.error ? 'alert' : 'status'}>
          {note.text}
        </p>
      ))}
      {realmImageBytes + added.current > LARGE_REALM_IMAGE_BYTES && (
        <p className="meta">This realm holds over 200 MB of pictures, so saving it takes longer.</p>
      )}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/bmp"
        multiple
        hidden
        onChange={(event) => {
          void add([...(event.currentTarget.files ?? [])]);
          event.currentTarget.value = '';
        }}
      />
      <button type="button" onClick={() => input.current?.click()}>
        + Add pictures
      </button>
      <p className="meta">PNG, JPEG, GIF, WebP or BMP, up to 10 MB each.</p>
    </section>
  );
}
